import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, Eye, EyeOff, Trash2, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await axios.get(`${API}/admin/reviews?${params.toString()}`);
      setReviews(data.reviews || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const counts = useMemo(() => {
    const c = { all: reviews.length, published: 0, hidden: 0 };
    reviews.forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1;
    });
    return c;
  }, [reviews]);

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const toggleStatus = async (r) => {
    const next = r.status === "published" ? "hidden" : "published";
    try {
      const { data } = await axios.patch(`${API}/admin/reviews/${r.id}`, { status: next });
      setReviews((rs) => rs.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Review ${next}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`);
      setReviews((rs) => rs.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      data-testid="admin-reviews-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Admin · Reviews
            </p>
            <h1 className="font-serif text-5xl md:text-7xl text-[color:var(--sr-primary)] leading-[0.95] tracking-tight">
              Voices,
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">curated.</em>
            </h1>
          </div>
        </div>

        {/* Filters */}
        <div className="border-y hairline-light py-6 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            {[
              { v: "all", label: `All (${counts.all})` },
              { v: "published", label: `Published (${counts.published || 0})` },
              { v: "hidden", label: `Hidden (${counts.hidden || 0})` },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setStatusFilter(opt.v)}
                data-testid={`reviews-filter-${opt.v}`}
                className={`px-4 py-2 label-eyebrow border transition-colors ${
                  statusFilter === opt.v
                    ? "border-[color:var(--sr-primary)] bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)]"
                    : "hairline text-[color:var(--sr-primary)] hover:border-[color:var(--sr-primary)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[220px] flex items-center gap-2 border-b hairline focus-within:border-[color:var(--sr-primary)]">
            <Search size={14} strokeWidth={1.5} className="text-[color:var(--sr-muted)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search title, body, name, product"
              data-testid="reviews-search"
              className="w-full bg-transparent outline-none py-2 text-[color:var(--sr-primary)] font-serif text-lg placeholder:text-[color:var(--sr-muted)]/60"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-[color:var(--sr-muted)] py-12">Loading…</p>
        ) : reviews.length === 0 ? (
          <div data-testid="admin-reviews-empty" className="border hairline p-12 text-center">
            <p className="font-serif text-3xl text-[color:var(--sr-primary)]">No reviews match.</p>
          </div>
        ) : (
          <ul
            className="divide-y divide-[color:var(--sr-border-light)] border-y hairline-light"
            data-testid="admin-reviews-list"
          >
            {reviews.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className={`py-6 grid grid-cols-12 gap-4 items-start ${r.status === "hidden" ? "opacity-50" : ""}`}
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="flex items-center gap-1 text-[color:var(--sr-accent)] mb-1">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => (
                      <Star key={i} size={12} strokeWidth={0} fill="currentColor" />
                    ))}
                  </div>
                  <p className="font-serif text-lg text-[color:var(--sr-primary)]">{r.name}</p>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--sr-muted)] mt-1">
                    {r.verified_purchase ? "Verified · " : ""}
                    {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                  {r.product_id && (
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--sr-accent)] mt-1">
                      {r.product_id}
                    </p>
                  )}
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="font-serif text-xl text-[color:var(--sr-primary)] leading-tight mb-1">
                    {r.title}
                  </p>
                  <p className="text-sm text-[color:var(--sr-text)]/85 leading-relaxed">
                    {r.body}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => toggleStatus(r)}
                    data-testid={`review-toggle-${r.id}`}
                    className="p-2 border hairline hover:border-[color:var(--sr-primary)] transition-colors"
                    aria-label={r.status === "published" ? "Hide" : "Publish"}
                  >
                    {r.status === "published" ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(r)}
                    data-testid={`review-delete-${r.id}`}
                    className="p-2 border hairline hover:border-[#8B2E4F] hover:text-[#8B2E4F] transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
